import {
  ApiAllUserApplicationsListQueryParams,
  useApiAllUserApplicationsList,
  useApiPvpApplicationsList,
  useApiPvpList,
} from "@/api/lctApIComponents";
import { DataTable } from "@/components/DataTable";
import { useAppSelector } from "@/hooks/reduxHooks";
import { useDates } from "@/hooks/useDates";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo, useState, useEffect } from "react";
import { StatusPill } from "../StatusPill";
import { DetailedApplication, StatusEnum } from "@/api/lctApISchemas";
import { useNavigate } from "@tanstack/react-router";
import { ApplicationStatus, statusOptions, TABLE_IDS } from "@/utils/constants";
import { FilterConfig, FilterDrawer } from "@/components/DataTable/Filters/TableFilters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ApplicationOverview } from "@/components/ApplicationOverview";

interface IProps {
  pvpId?: string;
}

export const UserApplications: React.FC<IProps> = ({ pvpId }) => {
  const token = useAppSelector((state) => state.user.authKey);
  const [selectedPvp, setSelectedPvp] = useState<string | undefined>();
  const [selectedApp, setSelectedApp] = useState<string | undefined>();
  const [selectedStatus, setSelectedStatus] = useState<string | undefined>();

  useEffect(() => {
    setSelectedPvp(pvpId);
    setSelectedApp(undefined);
    setSelectedStatus(undefined);
  }, [pvpId]);

  const queryParams = useMemo(() => {
    const params: ApiAllUserApplicationsListQueryParams = {};
    if (selectedPvp) {
      params["application__pvp"] = selectedPvp;
    } else {
      delete params["application__pvp"];
    }
    if (selectedStatus) {
      params["status"] = selectedStatus as StatusEnum;
    } else {
      delete params["status"];
    }
    if (selectedApp && selectedPvp) {
      params["application"] = selectedApp;
    } else {
      delete params["application"];
    }
    return params;
  }, [selectedApp, selectedStatus, selectedPvp]);

  const { data, isLoading } = useApiAllUserApplicationsList({
    headers: { Authorization: `Token ${token}` },
    queryParams,
  });

  const { data: pvpData, isLoading: pvpDataLoading } = useApiPvpList({ headers: { Authorization: `Token ${token}` } }, { staleTime: 1000 });
  const pvpOptions = pvpData?.results.map((pvp) => ({ label: pvp.title, value: pvp.id }));

  const { data: appData, isLoading: appDataLoading } = useApiPvpApplicationsList(
    { headers: { Authorization: `Token ${token}` }, pathParams: { pvp: selectedPvp || "" } },
    { enabled: !!pvpOptions && !!selectedPvp },
  );
  const appOptions = appData?.results.filter((app) => app.active).map((app) => ({ label: app.title, value: app.id }));

  const filterConfig: FilterConfig[] = [
    {
      label: "Certification Program",
      options: pvpOptions ?? [],
      selected: selectedPvp,
      onChange: setSelectedPvp,
      optionsLoading: pvpDataLoading,
    },
    selectedPvp && appOptions && appOptions.length > 1
      ? {
          label: "Certification Application",
          options: appOptions ?? [],
          selected: selectedApp,
          onChange: setSelectedApp,
          optionsLoading: pvpDataLoading || appDataLoading,
        }
      : undefined,
    {
      label: "Status",
      options: statusOptions ?? [],
      selected: selectedStatus,
      onChange: setSelectedStatus,
    },
  ];

  const navigate = useNavigate();

  const { formatDateToShortDate } = useDates();

  const navigateToApplication = (row: DetailedApplication) => {
    navigate({
      to: "/pvp/$pvpId",
      params: { pvpId: row.application.pvp.id },
      search: { userAppId: row.id, appId: row.application.id },
    });
  };

  const tableData = useMemo(() => {
    return data?.results ?? [];
  }, [data?.results]);

  const columns = useMemo(() => {
    const { accessor } = createColumnHelper<DetailedApplication>();
    return [
      accessor("user_app_id", {
        id: "user_app_id",
        header: "Application ID",
        cell: (info) => info.getValue() || "-",
        meta: {
          canCopy: true,
        },
      }),
      accessor("name", {
        id: "name",
        header: "Application Name",
        cell: (info) => info.getValue() || "-",
        meta: {
          canCopy: true,
        },
      }),
      accessor("application.pvp.title", {
        id: "pvp",
        header: "Certification Program",
        cell: (info) => info.getValue() || "-",
      }),
      accessor("application.title", {
        id: "application",
        header: "Certification Application",
        cell: (info) => info.getValue() || "-",
      }),
      accessor("created_at", {
        id: "created_at",
        header: "Started",
        cell: (info) => formatDateToShortDate(info.getValue()) || "-",
      }),
      accessor("updated_at", {
        id: "updated_at",
        header: "Last Saved",
        cell: (info) => formatDateToShortDate(info.getValue()) || "-",
      }),
      accessor("status", {
        id: "status",
        header: "Status",
        cell: ({ getValue }) => {
          const status = getValue();
          return <StatusPill applicationStatus={status} />;
        },
      }),
      accessor("carbon_score", {
        id: "carbon_score",
        header: "Carbon Score",
        cell: (info) => {
          const application = info.row.original;
          const canShowCarbonScore =
            application.status === ApplicationStatus.APPROVED || application.status === ApplicationStatus.REJECTED;
          return canShowCarbonScore ? (info.getValue() ?? "-") : "-";
        },
      }),
    ];
  }, [formatDateToShortDate]);

  const renderMobileView = () => {
    return (
      <div className="space-y-3 bg-slate-100 p-4 pb-8">
        <ApplicationOverview />
        <h2 className="w-full px-6 pt-4 text-2xl font-semibold">Applications</h2>
        <div className="flex justify-end">
          <FilterDrawer filters={filterConfig} />
        </div>
        {tableData.map((item) => (
          <Card className="w-full cursor-pointer" key={item.id} onClick={() => navigateToApplication(item)}>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-md line-clamp-2 font-medium text-black">{item.application.title}</CardTitle>{" "}
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="space-y-2">
                <span className="text-xs font-normal text-muted-foreground">{`Started: ${formatDateToShortDate(item.created_at)}`}</span>
                <br />
                <span className="text-xs font-normal text-muted-foreground">{`Last updated: ${formatDateToShortDate(item.updated_at)}`}</span>
                <div className="flex justify-between">
                  <StatusPill applicationStatus={item.status} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  const renderDesktopView = () => {
    return (
      <div className="flex flex-col gap-2">
        <div className="p-6">
          <ApplicationOverview />
        </div>
        <h2 className="w-full px-6 text-2xl font-semibold">Applications</h2>
        <DataTable<DetailedApplication>
          tableId={TABLE_IDS.USER_APPLICATIONS_USER}
          columns={columns as ColumnDef<DetailedApplication, unknown>[]}
          data={tableData}
          loading={isLoading}
          filterConfig={filterConfig}
          onRowClick={navigateToApplication}
        />
      </div>
    );
  };

  return (
    <div className="h-full max-h-[calc(100vh-200px)] overflow-y-auto">
      <div className="block md:hidden">{renderMobileView()}</div>
      <div className="hidden md:block">{renderDesktopView()}</div>
    </div>
  );
};
